import { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { MdOutlineTableChart, MdOutlineShowChart } from "react-icons/md";
import { formatMoney, formatNumber } from "./crmHelpers";
import {
  SERIES,
  TEXTO_EIXO,
  baseChartOptions,
  barPlotOptions,
  barDataLabels,
} from "./chartTheme";

/*
  Gráficos de uso da conta.

  **Dois gráficos, não um com dois eixos.** Agendamentos/comandas são
  contagens (dezenas) e receita é dinheiro (milhares). Num eixo duplo as duas
  curvas se cruzam onde as ESCALAS mandam, não onde os dados mandam — e todo
  cruzamento vira uma conclusão falsa sobre a conta. Separar é a única leitura
  honesta.

  As cores vêm de chartTheme.js, onde está registrada a validação delas.
*/

const UsoChart = ({ serie }) => {
  const categorias = serie.map((item) => item.label);

  const options = {
    ...baseChartOptions(categorias),
    colors: [SERIES.primaria, SERIES.secundaria],
    stroke: { curve: "smooth", width: 2 },
    markers: {
      size: 0,
      // Marcador só no hover, com 8px — alvo grande o bastante para o mouse
      // e ponto limpo quando a série tem 16 meses.
      hover: { size: 6, sizeOffset: 2 },
      strokeWidth: 2,
      strokeColors: "#ffffff",
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.18,
        opacityTo: 0.02,
        stops: [0, 100],
      },
    },
    tooltip: {
      ...baseChartOptions(categorias).tooltip,
      y: { formatter: (value) => formatNumber(value) },
    },
  };

  return (
    <ReactApexChart
      type="area"
      height={250}
      options={options}
      series={[
        { name: "Agendamentos", data: serie.map((item) => item.appointments) },
        { name: "Comandas", data: serie.map((item) => item.commands) },
      ]}
    />
  );
};

const ReceitaChart = ({ serie }) => {
  const categorias = serie.map((item) => item.label);

  const options = {
    ...baseChartOptions(categorias),
    colors: [SERIES.dinheiro],
    plotOptions: barPlotOptions(),
    dataLabels: barDataLabels((value) =>
      value >= 1000 ? `${(value / 1000).toFixed(1)}k` : Math.round(value)
    ),
    yaxis: {
      labels: {
        style: { colors: TEXTO_EIXO, fontSize: "11px" },
        formatter: (value) =>
          value >= 1000 ? `${Math.round(value / 1000)}k` : Math.round(value),
      },
    },
    tooltip: {
      ...baseChartOptions(categorias).tooltip,
      y: { formatter: (value) => formatMoney(value) },
    },
  };

  return (
    <ReactApexChart
      type="bar"
      height={170}
      options={options}
      series={[{ name: "Receita das comandas", data: serie.map((item) => item.revenue) }]}
    />
  );
};

const Legenda = ({ itens }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
    {itens.map((item) => (
      <span key={item.label} className="flex items-center gap-1.5 text-[11px] text-slate-600">
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: item.color }}
        />
        {item.label}
      </span>
    ))}
  </div>
);

/*
  Tabela equivalente ao gráfico.

  Não é só acessibilidade: é a resposta para "qual foi o número exato de
  julho?", que nenhuma curva responde. Fica atrás de um botão porque a
  pergunta frequente é a tendência, não o valor pontual.
*/
const Tabela = ({ serie }) => (
  <div className="max-h-[320px] overflow-auto">
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-white">
        <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <th className="py-2 pr-2">Mês</th>
          <th className="py-2 px-2 text-right">Agendamentos</th>
          <th className="py-2 px-2 text-right">Comandas</th>
          <th className="py-2 pl-2 text-right">Receita</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {[...serie].reverse().map((item, index) => (
          <tr key={item.month} className={index === 0 ? "text-slate-400" : ""}>
            <td className="py-1.5 pr-2 text-slate-600">
              {item.label}
              {index === 0 ? (
                <span className="ml-1 text-[10px] text-slate-400">
                  (em andamento)
                </span>
              ) : null}
            </td>
            <td className="py-1.5 px-2 text-right font-semibold text-slate-800">
              {formatNumber(item.appointments)}
            </td>
            <td className="py-1.5 px-2 text-right font-semibold text-slate-800">
              {formatNumber(item.commands)}
            </td>
            <td className="py-1.5 pl-2 text-right font-semibold text-slate-800">
              {item.revenue === undefined ? "—" : formatMoney(item.revenue)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AccountCharts = ({ lifetimeSeries = [], usageSeries = [] }) => {
  const [modo, setModo] = useState("grafico");

  /*
    A receita só existe nos 6 meses de `usageSeries`; a contagem vem dos 16 de
    `lifetimeSeries`. A tabela junta as duas pelo mês — e deixa a receita vazia
    onde o dado não existe, em vez de mostrar zero. Zero significaria "não
    faturou nada", que é uma afirmação diferente de "não sabemos".
  */
  const tabela = useMemo(() => {
    const receitaPorMes = new Map(
      usageSeries.map((item) => [item.month, item.revenue])
    );
    return lifetimeSeries.map((item) => ({
      ...item,
      revenue: receitaPorMes.get(item.month),
    }));
  }, [lifetimeSeries, usageSeries]);

  if (!lifetimeSeries.length) {
    return (
      <p className="px-4 py-10 text-center text-sm text-slate-400">
        Esta conta ainda não tem histórico de uso.
      </p>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Legenda
            itens={[
              { label: "Agendamentos", color: SERIES.primaria },
              { label: "Comandas", color: SERIES.secundaria },
            ]}
          />
          {/*
            O último ponto é sempre o mês corrente, com poucos dias de dados
            contra os 30 dos anteriores. Sem este aviso a curva despenca no fim
            de TODA conta saudável, e a ficha sugere colapso onde só há
            calendário.
          */}
          <span className="text-[11px] text-slate-400">
            o último mês ainda está em andamento
          </span>
        </div>

        <button
          type="button"
          onClick={() => setModo((v) => (v === "grafico" ? "tabela" : "grafico"))}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-pink-200 hover:text-pink-700"
        >
          {modo === "grafico" ? (
            <>
              <MdOutlineTableChart size={14} /> ver números
            </>
          ) : (
            <>
              <MdOutlineShowChart size={14} /> ver gráfico
            </>
          )}
        </button>
      </div>

      {modo === "tabela" ? (
        <Tabela serie={tabela} />
      ) : (
        <>
          <UsoChart serie={lifetimeSeries} />

          {usageSeries.length ? (
            <div className="mt-2 border-t border-slate-100 pt-3">
              <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: SERIES.dinheiro }}
                />
                Receita das comandas — últimos 6 meses
              </p>
              <ReceitaChart serie={usageSeries} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default AccountCharts;
